<script setup lang="ts" generic="T extends z.ZodRawShape">
import type { AnyFieldMeta } from '@tanstack/form-core';
import type { ButtonVariants as DsButtonVariants } from '~ds/components/ui/button';
import type { ResponseResult } from '~kui/types/formResult';
import type { InputTypeState } from '~kui/types/input';
import type { z } from 'zod';

import { useForm } from '@tanstack/vue-form';
import { Button } from '~ds/components/ui/button';
import { useI18n } from 'vue-i18n';

import type { FormConfig } from './types';

interface Props {
  formConfig: FormConfig<T>;
  submitButtonSize?: DsButtonVariants['size'];
  submitButtonWidth?: 'fit' | 'full';
  loading?: boolean;
  buttonVariant?: DsButtonVariants['variant'];
  formResult?: ResponseResult | null;
  fieldErrors?: Partial<Record<string, string[]>>;
  allowCleanSubmit?: boolean;
  fieldsWrapperClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  submitButtonSize: 'lg',
  submitButtonWidth: 'full',
  loading: false,
  buttonVariant: 'secondary',
  formResult: null,
  fieldErrors: () => ({}),
  allowCleanSubmit: false,
  fieldsWrapperClass: 'my-6 md:my-4 space-y-4 md:space-y-2',
});

const emit = defineEmits<{
  formReady: [];
  submit: [value: z.infer<z.ZodObject<T>>];
  change: [value: z.infer<z.ZodObject<T>>];
}>();
const { t } = useI18n();
const noChangesHintId = useId();

const isFormReady = ref(false);
const fieldValidity = reactive<Record<string, boolean>>({});

const form = useForm({
  defaultValues: {
    ...Object.fromEntries(Object.entries(props.formConfig.fields).map(([key, config]) => [key, config.initialValue])),
  },
  validators: {
    onChange: props.formConfig.schema,
    onMount: props.formConfig.schema,
  },
  listeners: {
    onMount: () => {
      isFormReady.value = true;
      emit('formReady');
    },
    onChange: () => {
      emit('change', form.state.values as z.infer<z.ZodObject<T>>);
    },
  },
  onSubmit: ({ value }) => {
    emit('submit', value as z.infer<z.ZodObject<T>>);
  },
});

const hasInvalidField = computed(() => Object.values(fieldValidity).includes(false));

/**
 * A save that cannot go through explains itself. Field errors only surface once a field has been
 * blurred, so a field the student never touched (required since before their profile existed,
 * say) has never shown why it blocks the form: blurring every field puts each reason on screen,
 * and focus lands on the first field at fault so the reveal has a landing point.
 */
const revealErrors = () => {
  Object.keys(props.formConfig.fields).forEach((key) => {
    form.setFieldMeta(key, meta => ({ ...meta, isBlurred: true }));
  });

  const firstInvalid = Object.keys(props.formConfig.fields).find(key =>
    fieldValidity[key] === false || form.getFieldMeta(key)?.errors.length);
  if (!firstInvalid) {
    return;
  }

  // The key lands on the field's input for components that declare an `id` prop, and on their
  // root element for those that don't; in the latter case the first control inside is the target.
  const host = document.getElementById(firstInvalid);
  const control = host?.matches('input, select, textarea, button')
    ? host
    : host?.querySelector<HTMLElement>('input, select, textarea, button');
  control?.focus();
};

const handleSubmit = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();

  if (!props.allowCleanSubmit && !form.state.isDirty) {
    return;
  }
  if (!form.state.canSubmit || hasInvalidField.value) {
    revealErrors();
    return;
  }

  form.handleSubmit();
};

const getFieldErrors = (key: string, validationMessage?: string): string[] => {
  const errors: string[] = [];
  if (fieldValidity[key] === false && validationMessage) {
    errors.push(validationMessage);
  }
  if (props.fieldErrors?.[key]?.length) {
    errors.push(...props.fieldErrors[key]);
  }
  return errors;
};

const getInputTypeState = (key: string, meta: AnyFieldMeta): InputTypeState => {
  if (props.fieldErrors?.[key]?.length) {
    return 'error';
  }
  if (fieldValidity[key] === false) {
    return 'error';
  }
  if (!meta.isDirty) {
    return undefined;
  }
  if (meta.isValid) {
    return 'valid';
  }
  return 'error';
}

/** Puts one field back to a value, for a host whose save partly failed after the form submitted. */
const setFieldValue = (key: string, value: string | number | boolean | null) => {
  form.setFieldValue(key, value);
};

defineExpose({ setFieldValue });
</script>

<template>
  <form @submit="handleSubmit">
    <!--
      A greyed-out submit button still submits: the second press goes out underneath the first and
      the two race whatever the first one started. Only a natively disabled fieldset stops the
      browser submitting the form, so `loading` holds the whole form inert rather than just dimming
      the button.
    -->
    <fieldset
      :disabled="loading"
      :aria-busy="loading"
      class="w-full min-w-0"
      :class="{ 'opacity-60': loading }"
    >
      <div :class="fieldsWrapperClass">
        <template v-for="(fieldConfig, key) in formConfig.fields" :key="key">
          <div :class="fieldConfig.wrapperClass">
            <slot :name="`${key}-leading`" />
            <form.Field :name="key">
              <template #default="{ field, state }">
                <div>
                  <component
                    :is="fieldConfig.component"
                    :id="field.name"
                    :name="field.name"
                    v-bind="fieldConfig.props as object"
                    :value="field.state.value"
                    :model-value="field.state.value"
                    :input-type-state="getInputTypeState(key, state.meta)"
                    @update:model-value="(e: unknown) => field.handleChange(e)"
                    @blur="field.handleBlur"
                    @valid="(isValid: boolean) => { fieldValidity[key] = isValid }"
                  />
                </div>
                <KUICoreFormFieldState
                  :state="state"
                  :field-key="key"
                  :hint="fieldConfig.hint"
                />
                <KUICoreInputErrors
                  v-if="getFieldErrors(key, fieldConfig.validationMessage).length"
                  :error-messages="getFieldErrors(key, fieldConfig.validationMessage)"
                />
              </template>
            </form.Field>
            <slot :name="`${key}-trailing`" />
          </div>
        </template>
      </div>
      <KUICoreInputErrors
        v-if="formResult && !formResult.success && formResult.message"
        :error-messages="[formResult.message]"
        class="mb-4"
      />
      <form.Subscribe>
        <template #default="{ isSubmitting }">
          <!--
            Dirty is the only thing that holds the button. Validity deliberately does not: an
            invalid field the student never touched (a profile saved before a field became
            required, say) would otherwise keep the save dead with no visible reason. A
            dirty-but-invalid submit lands in handleSubmit, which reveals the field errors.
            Held is aria-disabled rather than disabled, so the button stays in the tab order and
            says why it's off; handleSubmit ignores a clean submit.
          -->
          <div class="flex flex-wrap items-center gap-3">
            <Button
              type="submit"
              :loading="isSubmitting || !isFormReady || loading"
              :aria-disabled="!props.allowCleanSubmit && !form.state.isDirty ? 'true' : undefined"
              :aria-describedby="!props.allowCleanSubmit && !form.state.isDirty ? noChangesHintId : undefined"
              :variant="buttonVariant"
              :size="submitButtonSize"
              :class="submitButtonWidth === 'full' ? 'w-full' : undefined"
            >
              {{ formConfig.submitButtonText ?? 'Submit' }}
            </Button>
            <span
              v-if="!props.allowCleanSubmit && !form.state.isDirty"
              :id="noChangesHintId"
              class="sr-only"
            >{{ t('kui.form.noChanges') }}</span>
            <!-- Secondary actions that belong beside the submit, such as a resend link. -->
            <slot name="actions" />
          </div>
        </template>
      </form.Subscribe>
    </fieldset>
  </form>
</template>
